import { computed, ref, unref } from 'vue'
import { cloneDeep } from 'lodash-es'
import { FILTER_CACHE_PREFIX, FILTER_CACHE_VERSION } from '@/constants/filterCache'

const getSafeStorage = storage => {
  if (storage) return storage
  if (typeof window === 'undefined') {
    return {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {}
    }
  }

  return window.sessionStorage
}

const safeParse = value => {
  if (!value) return null

  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

const safeStringify = value => {
  try {
    return JSON.stringify(value)
  } catch (error) {
    return ''
  }
}

const buildStorageKey = ({ pageKey, userId }) => {
  return [FILTER_CACHE_PREFIX, FILTER_CACHE_VERSION, pageKey, unref(userId) || 'anonymous'].join(
    ':'
  )
}

export function useFilterCache(options) {
  const {
    pageKey,
    defaultParams,
    userId,
    storage,
    getInitParams,
    normalize = params => params,
    serialize = params => params,
    deserialize = params => params
  } = options

  const currentStorage = getSafeStorage(storage)
  const initialized = ref(false)
  const queryParams = ref(normalize(cloneDeep(defaultParams)))

  const storageKey = computed(() => {
    return buildStorageKey({
      pageKey,
      userId
    })
  })

  const setQueryParams = params => {
    const nextParams = normalize(cloneDeep(params))
    queryParams.value = nextParams
    return cloneDeep(nextParams)
  }

  const readCache = () => {
    const parsed = safeParse(currentStorage.getItem(storageKey.value))
    if (!parsed) {
      return null
    }

    return deserialize(parsed)
  }

  const updateCache = (params = queryParams.value) => {
    const nextParams = setQueryParams(params)
    const cacheValue = serialize(cloneDeep(nextParams))
    const content = safeStringify(cacheValue)

    if (content) {
      currentStorage.setItem(storageKey.value, content)
    }

    return cloneDeep(nextParams)
  }

  const initFromCache = () => {
    if (initialized.value) {
      return cloneDeep(queryParams.value)
    }

    const initParams = typeof getInitParams === 'function' ? getInitParams() || {} : {}
    const cacheParams = readCache() || {}
    const mergedParams = {
      ...cloneDeep(defaultParams),
      ...cloneDeep(initParams),
      ...cloneDeep(cacheParams)
    }

    initialized.value = true
    return setQueryParams(mergedParams)
  }

  const resetCache = () => {
    const resetParams = setQueryParams(defaultParams)
    const cacheValue = serialize(cloneDeep(resetParams))
    currentStorage.setItem(storageKey.value, safeStringify(cacheValue))
    return cloneDeep(resetParams)
  }

  const clearCache = () => {
    currentStorage.removeItem(storageKey.value)
    return setQueryParams(defaultParams)
  }

  return {
    queryParams,
    storageKey,
    initialized,
    initFromCache,
    updateCache,
    resetCache,
    clearCache,
    readCache,
    setQueryParams
  }
}
